import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'src/environments/environments';

@Injectable({ providedIn: 'root' })
export class ApartmentService {

    private subject = new BehaviorSubject<string>('Initial Value')
    current = this.subject.asObservable()

    private arrayData = new BehaviorSubject([])
    currentArray = this.arrayData.asObservable()

    constructor(
        private router: Router,
        private http: HttpClient
    ) { }
    // Apartment
    apartmentDropdown() {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/apartment/dropdown`).pipe(map(user => user));
    }

    // Apartment dropdown for coupon property targeting (restaurant / laundry coupons)
    getCouponApartmentsDropdown(search: string = '') {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/coupon/apartments-dropdown?search=${search}`).pipe(map(res => res));
    }

    // Audit Logs
    getAuditLogs(type: 'apartment' | 'restaurant' | 'laundry', page: number, fromDate: string, toDate: string, search: string, category: string, limit?: number) {
        let url = `${environment.apiUrl}${environment.apiPrefix}/${type}/audit-logs/list?page=${page}`;
        if (limit) url += `&limit=${limit}`;
        if (fromDate) url += `&fromDate=${fromDate}`;
        if (toDate) url += `&toDate=${toDate}`;
        if (search) url += `&search=${search}`;
        if (category) url += `&category=${category}`;
        return this.http.get<any>(url).pipe(map(res => res));
    }

    getAuditLogsCount(type: 'apartment' | 'restaurant' | 'laundry') {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/${type}/audit-logs/count`).pipe(map(res => res));
    }

    // Coupon
    createApartmentCoupon(formData: FormData) {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/coupon/create`, formData).pipe(map(user => user));
    }

    getApartmentCouponList(page: any, search: any, status: any) {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/coupon/list?page=${page}&search=${search}&status=${status}`).pipe(map(user => user));
    }

    getApartmentCouponById(id: any) {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/coupon/getById/${id}`).pipe(map(user => user));
    }

    updateApartmentCoupon(id: any, formData: FormData) {
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/coupon/update/${id}`, formData).pipe(map(user => user));
    }

    deleteApartmentCoupon(id: any) {
        return this.http.delete<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/coupon/delete/${id}`).pipe(map(user => user));
    }

    updateApartmentCouponStatus(id: any, status: any) {
        const formData = { status };
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/coupon/update/${id}`, formData).pipe(map(user => user));
    }

    getApartmentUserDropdown(propertyId: any) {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/customer/list?page=1&status=1&propertyId=${propertyId}`).pipe(map(user => user));
    }

    getApartmentItemDropdown(categoryId: any) {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/item/dropdown?categoryId=${categoryId}`).pipe(map(user => user));
    }

    getApartmentCategoryDropdown(status: any) {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/room-service/category/dropdown`).pipe(map(user => user));
    }

    // Residential Settings — Cross-Apartment Browsing (single apartment-wide toggle)
    getCrossApartmentVisibility() {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/cross-apartment-visibility`).pipe(map(res => res));
    }

    updateCrossApartmentVisibility(allowCrossApartmentBrowsing: boolean) {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/cross-apartment-visibility`, { allowCrossApartmentBrowsing }).pipe(map(res => res));
    }

    // Properties / Units
    getUnitList(page: number, filters: { search?: string; vacancyStatus?: string; status?: string; unitType?: string; buildingId?: string; furnishing?: string; limit?: number } = {}) {
        let url = `${environment.apiUrl}${environment.apiPrefix}/apartment/residential/unit/list?page=${page}`;
        if (filters.search) url += `&search=${filters.search}`;
        if (filters.vacancyStatus) url += `&vacancyStatus=${filters.vacancyStatus}`;
        if (filters.status !== undefined && filters.status !== '') url += `&status=${filters.status}`;
        if (filters.unitType) url += `&unitType=${filters.unitType}`;
        if (filters.buildingId) url += `&buildingId=${filters.buildingId}`;
        if (filters.furnishing) url += `&furnishing=${filters.furnishing}`;
        if (filters.limit) url += `&limit=${filters.limit}`;
        return this.http.get<any>(url).pipe(map(res => res));
    }

    // Full unit document — buildingName + parkingSlots[]
    getUnitById(id: string) {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/unit/view/${id}`).pipe(map(res => res));
    }

    getUnitStats() {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/unit/vacancy-count`).pipe(map(res => res));
    }

    createUnit(body: any) {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/unit/add`, body).pipe(map(res => res));
    }

    updateUnit(id: string, body: any) {
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/unit/update/${id}`, body).pipe(map(res => res));
    }

    deleteUnit(id: string) {
        return this.http.delete<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/unit/delete/${id}`).pipe(map(res => res));
    }

    // Building Dropdown
    getBuildingDropdown(search: string = '') {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/building/dropdown?search=${search}`).pipe(map(res => res));
    }

    // Active Asset Manager staff — for mapping a unit's Asset Manager
    getAssetManagerDropdown() {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/staff/dropdown?designation=ASSET_MANAGER`).pipe(map(res => res));
    }

    // Unit Dropdown (residential)
    getResidentialUnitDropdown(search: string = '', buildingId: string = '', includeOccupied: boolean = false) {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/unit/dropdown?search=${search}&buildingId=${buildingId}&includeOccupied=${includeOccupied}`).pipe(map(res => res));
    }

    getParkingSlotList(unitId: string, occupancyStatus: string = '', search: string = '') {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/unit/parking-slot-list?unitId=${unitId}&occupancyStatus=${occupancyStatus}&search=${search}`).pipe(map(res => res));
    }

    // Building
    getBuildingList(page: number, search: string) {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/building/list?page=${page}&search=${search}`).pipe(map(res => res));
    }

    createBuilding(body: any) {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/building/add`, body).pipe(map(res => res));
    }

    updateBuilding(id: string, body: any) {
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/building/update/${id}`, body).pipe(map(res => res));
    }

    deleteBuilding(id: string) {
        return this.http.delete<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/building/delete/${id}`).pipe(map(res => res));
    }

    // Unit Type
    getUnitTypeList(page: number, search: string) {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/unit-type/list?page=${page}&search=${search}`).pipe(map(res => res));
    }

    createUnitType(body: any) {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/unit-type/add`, body).pipe(map(res => res));
    }

    updateUnitType(id: string, body: any) {
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/unit/update/${id}`, body).pipe(map(res => res));
    }

    deleteUnitType(id: string) {
        return this.http.delete<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/unit/delete/${id}`).pipe(map(res => res));
    }

    // Residential Service Category
    // image is optional
    addResidentialServiceCategory(name: string, description: string, image: File | null, icon?: File | null) {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('description', description);
        if (image) {
            formData.append('image', image);
        }
        if (icon) {
            formData.append('icon', icon);
        }
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/service-category/add`, formData).pipe(map(res => res));
    }

    updateResidentialServiceCategory(id: string, name: string, description: string, image: File | null, icon?: File | null) {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('description', description);
        if (image) {
            formData.append('image', image);
        }
        if (icon) {
            formData.append('icon', icon);
        }
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/service-category/update/${id}`, formData).pipe(map(res => res));
    }

    getResidentialServiceCategoryById(id: string) {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/service-category/view/${id}`).pipe(map(res => res));
    }
    getResidentialServiceCategoryList(page: any, search: any, status: any) {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/service-category/list?search=${search}&page=${page}&status=${status}`).pipe(map(res => res));
    }
    blockResidentialServiceCategory(id: string, status: number) {
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/service-category/update/${id}`, { status }).pipe(map(res => res));
    }
    getResidentialServiceCategoryDropdown(search: string = '') {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/service-category/dropdown?search=${search}`).pipe(map(res => res));
    }

    // Residential Service Item
    addResidentialServiceItem(name: string, categoryId: string, icon?: File | string | null, type?: string) {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('categoryId', categoryId);
        if (type) {
            formData.append('type', type);
        }
        if (icon) {
            formData.append('icon', icon);
        }
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/service-item/add`, formData).pipe(map(res => res));
    }
    updateResidentialServiceItem(id: string, name: string, categoryId: string, status?: number, icon?: File | string | null, type?: string) {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('categoryId', categoryId);
        if (status !== undefined) {
            formData.append('status', status.toString());
        }
        if (type) {
            formData.append('type', type);
        }
        if (icon) {
            formData.append('icon', icon);
        }
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/service-item/update/${id}`, formData).pipe(map(res => res));
    }
    getResidentialServiceItemById(id: string) {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/service-item/view/${id}`).pipe(map(res => res));
    }
    getResidentialServiceItemList(page: any, search: any, status: any, categoryId: string = '') {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/service-item/list?search=${search}&page=${page}&status=${status}&categoryId=${categoryId}`).pipe(map(res => res));
    }
    blockResidentialServiceItem(id: string, name: string, categoryId: string, status: number) {
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/service-item/update/${id}`, { name, categoryId, status }).pipe(map(res => res));
    }

    // Unit Excel
    downloadUnitExcel() {
        return this.http.get(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/excel/unit-sample-download`, { responseType: 'blob' });
    }

    uploadUnitExcel(file: File) {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/excel/unit-upload`, formData).pipe(map(res => res));
    }

    // Residential Service Orders
    getResidentialOrders(
        orderStatus: number,
        page: number,
        fromDate: string = '',
        toDate: string = '',
        priority: string = '',
        invoiceNumber: string = '',
        userName: string = '',
        userContactNo: string = '',
        paidStatus: string = ''
    ) {
        return this.http.get<any>(
            `${environment.apiUrl}${environment.apiPrefix}/apartment/residential/order/list?orderStatus=${orderStatus}&page=${page}&fromDate=${fromDate}&toDate=${toDate}&priority=${priority}&search_invoiceNumber=${invoiceNumber}&search_userName=${userName}&search_userContactNo=${userContactNo}&paidStatus=${paidStatus}`
        ).pipe(map(res => res));
    }

    getResidentialOrderCount() {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/order/count`).pipe(map(res => res));
    }

    updateResidentialOrderStatus(id: string, payload: object) {
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/order/update-status/${id}`, payload).pipe(map(res => res));
    }

    confirmResidentialCod(id: string) {
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/order/confirm-cod/${id}`, {}).pipe(map(res => res));
    }

    // Technician list for the Assign Technician modal (Accepted -> Technician Assigned)
    getTechnicianOptions() {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/order/technician-list`).pipe(map(res => res));
    }

    assignTechnician(id: string, payload: { scheduledDate: string; timeFrom: string; timeTo: string; technicianIds: string[] }) {
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/order/assign-technician/${id}`, payload).pipe(map(res => res));
    }

    // Second assignment step — after the estimate is paid (orderStatus 7,
    // inProgress), admin assigns who actually does the work.
    assignWorkTechnician(id: string, payload: { workScheduledDate: string; workTimeFrom: string; workTimeTo: string; technicianIds: string[] }) {
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/order/assign-work-technician/${id}`, payload).pipe(map(res => res));
    }

    // Approve maintenance estimate and assign technician
    approveMaintenanceEstimate(id: string, payload: { workScheduledDate: string; workTimeFrom: string; workTimeTo: string; technicianIds: string[] }) {
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/order/approve-maintenance-estimate/${id}`, payload).pipe(map(res => res));
    }

    getGateVisitorList(page: number, staffStatus: string = '', fromDate: string = '', toDate: string = '',
        visitorName: string = '', visitorPhone: string = '', flatNumber: string = '', visitorType: string = '') {
        return this.http.get<any>(
            `${environment.apiUrl}${environment.apiPrefix}/apartment/gate/visitor/list?page=${page}&filter_status=${staffStatus}&fromDate=${fromDate}&toDate=${toDate}&search_visitorName=${visitorName}&search_visitorPhone=${visitorPhone}&search_flatNumber=${flatNumber}&visitorType=${visitorType}`
        ).pipe(map(res => res));
    }

    getGateDeliveryList(page: number, limit: number, search: string = '', fromDate: string = '', toDate: string = '') {
        return this.http.get<any>(
            `${environment.apiUrl}${environment.apiPrefix}/apartment/gate/delivery/history?page=${page}&limit=${limit}&search=${search}&fromDate=${fromDate}&toDate=${toDate}`
        ).pipe(map(res => res));
    }

    getGateHouseholdWorkerLogs(page: number, limit: number, workerId: string = '') {
        return this.http.get<any>(
            `${environment.apiUrl}${environment.apiPrefix}/apartment/gate/household-worker/logs?page=${page}&limit=${limit}&search=${workerId}`
        ).pipe(map(res => res));
    }

    // Gate Staff working-status logs
    getGateStaffWorkingStatusLogs(page: number, limit: number, search: string = '', fromDate: string = '', toDate: string = '') {
        return this.http.get<any>(
            `${environment.apiUrl}${environment.apiPrefix}/apartment/gate/profile/working-status/logs?page=${page}&limit=${limit}&search=${search}&fromDate=${fromDate}&toDate=${toDate}`
        ).pipe(map(res => res));
    }

    // Digital visitor log (building visitors, no approval) — fromDate/toDate (YYYY-MM-DD) sent together or not at all
    getDigitalVisitorList(page: number, limit: number, search: string = '', fromDate: string = '', toDate: string = '') {
        const dates = fromDate && toDate ? `&fromDate=${fromDate}&toDate=${toDate}` : '';
        return this.http.get<any>(
            `${environment.apiUrl}${environment.apiPrefix}/apartment/gate/digital-visitor/list?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}${dates}`
        ).pipe(map(res => res));
    }

    // exitTime: ISO8601; the server uses "now" when it's left out
    recordDigitalVisitorExit(id: string, exitTime?: string) {
        return this.http.put<any>(
            `${environment.apiUrl}${environment.apiPrefix}/apartment/gate/digital-visitor/exit/${id}`,
            exitTime ? { exitTime } : {}
        ).pipe(map(res => res));
    }

    // Gate Staff attendance — hours per working status, one day (date) or summed over fromDate–toDate.
    // Dates are YYYY-MM-DD in Asia/Dubai; leave all empty for today.
    getGateStaffAttendance(page: number, limit: number, search: string = '', fromDate: string = '', toDate: string = '') {
        return this.http.get<any>(
            `${environment.apiUrl}${environment.apiPrefix}/apartment/gate/staff/working-status/report?page=${page}&limit=${limit}&search=${search}&fromDate=${fromDate}&toDate=${toDate}`
        ).pipe(map(res => res));
    }

    // Technician working hours — time on completed maintenance jobs; fromDate/toDate (YYYY-MM-DD,
    // by job completedAt) must be sent together or not at all
    getTechnicianAttendance(page: number, limit: number, search: string = '', fromDate: string = '', toDate: string = '') {
        return this.http.get<any>(
            `${environment.apiUrl}${environment.apiPrefix}/apartment/residential/order/technician-working-hours?page=${page}&limit=${limit}&search=${search}&fromDate=${fromDate}&toDate=${toDate}`
        ).pipe(map(res => res));
    }

    downloadResidentialCategoryExcel() {
        return this.http.get(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/excel/category-sample-download`, { responseType: 'blob' });
    }

    residentialCategoryExcelUpload(file: any) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/excel/category-upload`, formData).pipe(map(res => res));
    }

    downloadResidentialItemExcel() {
        return this.http.get(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/excel/item-sample-download`, { responseType: 'blob' });
    }

    residentialItemExcelUpload(file: any) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/residential/excel/item-upload`, formData).pipe(map(res => res));
    }

    // Facility (Amenity)
    addFacility(body: any) {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/facility/add`, body).pipe(map(res => res));
    }

    updateFacility(id: string, body: any) {
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/facility/update/${id}`, body).pipe(map(res => res));
    }

    getFacilityById(id: string) {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/facility/view/${id}`).pipe(map(res => res));
    }

    getFacilityList(page: any, search: string = '', status: any = '') {
        let url = `${environment.apiUrl}${environment.apiPrefix}/apartment/facility/list?page=${page}`;
        if (search) url += `&search=${search}`;
        if (status !== '' && status !== null && status !== undefined) url += `&status=${status}`;
        return this.http.get<any>(url).pipe(map(res => res));
    }

    getFacilityDropdown(search: string = '') {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/facility/dropdown?search=${search}`).pipe(map(res => res));
    }

    toggleFacilityStatus(id: string) {
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/facility/toggle-status/${id}`, {}).pipe(map(res => res));
    }

    deleteFacility(id: string) {
        return this.http.delete<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/facility/delete/${id}`).pipe(map(res => res));
    }

    // Pure preview — nothing persisted. Without `date`, returns the generic
    // weekly pattern; with `date`, resolves that date against any active
    // override.
    getFacilityPreviewSlots(id: string, date: string = '') {
        let url = `${environment.apiUrl}${environment.apiPrefix}/apartment/facility/${id}/preview-slots`;
        if (date) url += `?date=${date}`;
        return this.http.get<any>(url).pipe(map(res => res));
    }

    // Slot Instance (Slot Management screen) — never created directly, only
    // read/toggled.
    getSlotInstanceList(facilityId: string, date: string) {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/facility/slot-instance/list?facilityId=${facilityId}&date=${date}`).pipe(map(res => res));
    }

    toggleSlotInstanceStatus(id: string, reason: string = '') {
        const body = reason ? { reason } : {};
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/facility/slot-instance/toggle-status/${id}`, body).pipe(map(res => res));
    }

    // Special Date Override
    addFacilityOverride(body: any) {
        return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/facility/override/add`, body).pipe(map(res => res));
    }

    updateFacilityOverride(id: string, body: any) {
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/facility/override/update/${id}`, body).pipe(map(res => res));
    }

    deleteFacilityOverride(id: string) {
        return this.http.delete<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/facility/override/delete/${id}`).pipe(map(res => res));
    }

    getFacilityOverrideById(id: string) {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/facility/override/view/${id}`).pipe(map(res => res));
    }

    // facilityId omitted lists every override across the apartment (the
    // property-level "Special Dates" page).
    getFacilityOverrideList(facilityId: string = '', fromDate: string = '', toDate: string = '') {
        let url = `${environment.apiUrl}${environment.apiPrefix}/apartment/facility/override/list?`;
        const params: string[] = [];
        if (facilityId) params.push(`facilityId=${facilityId}`);
        if (fromDate) params.push(`fromDate=${fromDate}`);
        if (toDate) params.push(`toDate=${toDate}`);
        return this.http.get<any>(url + params.join('&')).pipe(map(res => res));
    }

    // Facility Booking (Booking Monitor) — read-only ledger, no admin
    // approval step; the only action available is Reject on a still-
    // "confirmed" row.
    getFacilityBookingList(facilityId: string = '', status: string = '', search: string = '', page: any = 1, limit: any = 10, fromDate: string = '', toDate: string = '') {
        let url = `${environment.apiUrl}${environment.apiPrefix}/apartment/facility/booking/list?page=${page}&limit=${limit}`;
        if (facilityId) url += `&facilityId=${facilityId}`;
        if (status) url += `&status=${status}`;
        if (search) url += `&search=${search}`;
        if (fromDate) url += `&fromDate=${fromDate}`;
        if (toDate) url += `&toDate=${toDate}`;
        return this.http.get<any>(url).pipe(map(res => res));
    }

    getFacilityBookingById(id: string) {
        return this.http.get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/facility/booking/view/${id}`).pipe(map(res => res));
    }

    rejectFacilityBooking(id: string, reason: string = '') {
        const body = reason ? { reason } : {};
        return this.http.put<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/facility/booking/reject/${id}`, body).pipe(map(res => res));
    }
}